import { getBscGlobalState, sendPackageOperationalEvent } from '../../../common/utils';
import { isProcessingAllowedByPreferences } from '../../../common/utils/item-processing';
import { Logger } from '../../../common/utils/logger';
import { BSCIndexedDB } from '../../../services/indexed-db-service';
import { CategoryStorageType, Status, type SetItemResult } from '../../../types';
import { WebStorageType } from '../types';

// SET COOKIE ANALOGUE
/**
 * Sets a WebStorage item asynchronously. This queries user preferences before setting
 * the item, so it must be used for any items which are not strictly necessary.
 */
export const setStorageItem = async (
	itemKey: string,
	value: string,
	storageType: WebStorageType,
	productName: string | undefined,
	browserStorage: Storage,
): Promise<SetItemResult> => {
	// `disableWebStorageConsentChecks` is the web-storage analogue of
	// `disableIndexedDbConsentChecks`. When set, we still emit analytics but always allow
	// the write. This mirrors the behaviour of `AtlIndexedDb.open` when consent checks are
	// disabled there.
	const disableWebStorageConsentChecks =
		getBscGlobalState().disableWebStorageConsentChecks ?? false;
	if (disableWebStorageConsentChecks) {
		const eventAttributes = {
			itemKey,
			storageType,
			consentChecksDisabled: true,
		};
		try {
			browserStorage.setItem(itemKey, value);
			await BSCIndexedDB.setRecord(itemKey);
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedAtlBrowserStorageSetItemError',
				message: `${storageType} failed to setItem with consent checks disabled. ${e.message || ''}`,
				attributes: eventAttributes,
			});
			return { status: Status.ERROR, error: e };
		}
		try {
			const shouldSetItem = await isProcessingAllowedByPreferences({
				itemKey,
				storageType:
					storageType === WebStorageType.LocalStorage
						? CategoryStorageType.LocalStorage
						: CategoryStorageType.SessionStorage,
				productName,
			});
			sendPackageOperationalEvent({
				action: 'usedAtlBrowserStorageSetItem',
				attributes: {
					...eventAttributes,
					wasRejected: !shouldSetItem,
				},
			});
		} catch (e: any) {
			Logger.errorWithOperationalEvent({
				action: 'usedAtlBrowserStorageSetItemError',
				attributes: {
					...eventAttributes,
				},
				message: `Failed to emit analytics for setStorageItem with consent checks disabled. ${e.message || ''}`,
			});
		}

		return { status: Status.SUCCESS };
	}
	// user belongs to an org that opted into cookies management and we should respect their preferences

	// Flag enabled, user belongs to an org that opted into cookies management and we should respect their preferences
	const shouldSetItem = await isProcessingAllowedByPreferences({
		itemKey,
		storageType:
			storageType === WebStorageType.LocalStorage
				? CategoryStorageType.LocalStorage
				: CategoryStorageType.SessionStorage,
		productName,
	});

	const eventAttributes = {
		wasRejected: !shouldSetItem,
		itemKey,
		storageType,
		consentChecksDisabled: false,
	};
	try {
		sendPackageOperationalEvent({
			action: 'usedAtlBrowserStorageSetItem',
			attributes: eventAttributes,
		});

		if (shouldSetItem) {
			// Store the item as a record in our IDB service to track that we've seen it before.
			// That way, when we can confirm/deny that this has been set by our service when we check again.
			// If it fails for some reason, it will be caught before actually setting the item
			await BSCIndexedDB.setRecord(itemKey);
			browserStorage.setItem(itemKey, value);
			return { status: Status.SUCCESS };
		} else {
			return { status: Status.BLOCKED };
		}
	} catch (e: any) {
		Logger.errorWithOperationalEvent({
			action: 'usedAtlBrowserStorageSetItemError',
			attributes: eventAttributes,
			message: `Failed to set ${storageType} item. ${e.message || ''}`,
		});
		return { status: Status.ERROR, error: e };
	}
};
